import React, { useMemo, useState, forwardRef, useEffect } from 'react';
import useDate from '~/react-hooks/use-date';
import { Watched, Clicked } from '~/App/Modules/Common/Components/Icons';
import AssetDetails from '~/App/Modules/MyLibrary/Components/AssetDetails';
import AssetContact from '~/App/Modules/MyLibrary/Components/AssetContact';
import ResourceTagBar from '~/App/Modules/Tags/Components/ResourceTagBar';
import { TaggableResourceType } from '~/types/models/tags';
import { useTaggableResourcePage } from '../hooks/use-taggable-resource-page';
import { useThumbnailWithFallback } from '../hooks/use-thumbnail-with-fallback';
import { EmbedTile } from '@vimily/bonjoro-ui-components-v2';
import {
  EmbedTileProps,
  EmbedTileType,
  MediaType,
} from '@vimily/bonjoro-ui-components-v2/EmbedTile';
import { UserAsset } from '../Api/UserAsset';
import UserAssetActionLinks from './UserAssetActionLinks';
import { getRichTextFromSlateData, parseRichTextToString } from '@vimily/bonjoro-ui-components-v2/RichTextEditor';

export type UserAssetListItemProps = {
  userAsset: UserAsset;
  onTagUpdated: () => void;
  onThumbnailLoad?: () => void;
  hideTagBar?: boolean;
  hideContactTagging?: boolean;
  hideInteractionStats?: boolean;
  showDownloadLink?: boolean;
};

const UserAssetListItem = forwardRef<HTMLDivElement, UserAssetListItemProps>(
  (props, detailsContainerRef) => {
    const {
      userAsset,
      onTagUpdated,
      onThumbnailLoad,
      hideTagBar = false,
      hideContactTagging = false,
      hideInteractionStats = false,
      showDownloadLink = false,
    } = props;

    const taggableResourcePage = useTaggableResourcePage();
    const { relative } = useDate(userAsset.created_at);
    const [showManageContactModal, setShowManageContactModal] = useState(false);
    const { thumbnail, onThumbnailLoadError } = useThumbnailWithFallback({
      imageUrl: userAsset?.video?.media.thumbnail_source,
    });

    const editUrl = useMemo(
      () =>
        userAsset?.video
          ? `/recordings/${userAsset?.video?.type}/${userAsset?.video?.id}/edit`
          : '',
      [userAsset]
    );

    const { views, clicks } = useMemo(
      () => userAsset?.video?.interactions || {},
      [userAsset]
    );

    const hasContact = userAsset?.author !== null;

    const hideStat = hasContact || hideInteractionStats;

    const openManageContactModal = () => setShowManageContactModal(true);

    useEffect(() => {
      if (
        !userAsset?.video &&
        userAsset?.content_rich &&
        userAsset?.content_rich.length > 0
      ) {
        onThumbnailLoad?.();
      }
    }, []);

    const embedTileProps: EmbedTileProps = useMemo(() => {
      const props = {
        type: EmbedTileType.BonjoroResponse,
        className: '!min-h-[195px]',
        playable: true,
        hideDateTime: true,
        hideReaction: true,
      };

      if (userAsset?.author?.name) {
        props['profile'] = {
          name: userAsset?.author?.name ?? undefined,
          imageUrl: userAsset?.author?.image ?? undefined,
          tag: userAsset?.author?.position ?? undefined,
        };
      }

      if (userAsset?.video) {
        props['media'] = [
          {
            type: MediaType.Video,
            url: userAsset?.video?.video_url,
            transcription: userAsset?.video?.transcript?.captions?.sentences,
            transcriptionClassName: '!text-xs',
            thumbnailUrl: thumbnail,
            onThumbnailLoad: onThumbnailLoad,
            onThumbnailLoadError,
          },
        ];
      }

      if (userAsset?.content_rich != null) {
        const richText = getRichTextFromSlateData(userAsset?.content_rich);

        if (parseRichTextToString(richText) != '') {
          props['text'] = richText;
        }
      }

      return props;
    }, [userAsset, thumbnail]);

    return (
      <>
        <EmbedTile {...embedTileProps} />
        <div ref={detailsContainerRef}>
          <AssetDetails
            name={userAsset?.name}
            redirectUrl={editUrl}
            actionLinksComponent={
              <UserAssetActionLinks
                userAsset={userAsset}
                openManageContactModal={showManageContactModal}
                onRequestOpenManageContactModal={openManageContactModal}
                onRequestCloseManageContactModal={() =>
                  setShowManageContactModal(false)
                }
                hideContactTagging={hideContactTagging}
                showDownloadLink={showDownloadLink}
              />
            }
          >
            {hasContact && (
              <AssetContact
                name={userAsset.author?.name as string}
                onClick={openManageContactModal}
              />
            )}
            {!hideStat && (
              <>
                <Watched /> <span className='ml-1.5'>{views ?? 0}</span>
              </>
            )}
            {!hideStat && !!clicks && <Clicked />}
            {relative}
          </AssetDetails>
          {!hideTagBar && (
            <div>
              <ResourceTagBar
                emptyText='Add a tag'
                onTagResourceSuccess={onTagUpdated}
                onUnTagResourceSuccess={onTagUpdated}
                resource={userAsset}
                resourceType={TaggableResourceType.UserAsset}
                sourcePage={taggableResourcePage}
              />
            </div>
          )}
        </div>
      </>
    );
  }
);

export default UserAssetListItem;
